  <template>
    <div :key="componentKey">
      <div class="controls" :style="controlsStyle">
        <div class="travel-mode-buttons sm-font" :style="travelModeButtonsStyle">
          <button 
            @click="setTravelMode('DRIVING')"
            :style="getTravelModeButtonStyle('DRIVING')"
          >
            Drive
          </button>
          <button 
            @click="setTravelMode('WALKING')"
            :style="getTravelModeButtonStyle('WALKING')"
          >
            Walk
          </button>
          
        </div>
        <button 
          v-if="positions.length >= 2" 
          @click="openDirections"
          class="update sm-font flexy p4"
        >
          <i class="fa fa-map-marker-alt r4"></i> Maps
        </button>
        <button 
          v-if="routeInfo" 
          @click="toggleEditMode"
          class="update sm-font flexy p4"
        >
          <i class="fa fa-pencil-alt r4"></i> {{ editMode ? 'Save' : 'Adjust' }}
        </button>
      </div>
      
      <div v-if="routeInfo && !editMode" class="container b8 p8">
        <itable :tabledata="routeTableData" title="Route Information" />
      </div>
      
      <div v-if="editMode && geocodedResults.length > 0" class="location-list" >
        <div class="location-header"
          :style="{ padding: '8px 12px', background: this.$store.state.app.style['sub-container'] || '#f0f0f0', borderBottom: `1px solid ${this.$store.state.app.style.color || '#e0e0e0'}`, fontWeight: '600', fontSize: '12px' }">
          Route Stops ({{ editableStops.length }})
          <button @click="addStop" :style="addButtonStyle">+ Add Stop</button>
        </div>
        <ul style="list-style: none; padding: 0; margin: 0;">
          <li v-for="(stop, idx) in editableStops" :key="idx" draggable="true" @dragstart="onDragStart($event, idx)"
            @dragover.prevent @drop="onDrop($event, idx)" :style="locationItemStyle">
            <span style="margin-right: 6px; color: #666; font-size: 10px;">{{ idx + 1 }}.</span>
            <input 
              v-model="stop.address" 
              :style="stopInputStyle"
              placeholder="Enter address..."
              @keyup.enter="updateStop(idx)"
            />
            <div class="stop-actions">
              <button @click="moveStopUp(idx)" :disabled="idx === 0" :style="actionButtonStyle">↑</button>
              <button @click="moveStopDown(idx)" :disabled="idx === editableStops.length - 1" :style="actionButtonStyle">↓</button>
              <button @click="removeStop(idx)" :disabled="editableStops.length <= 1" :style="removeButtonStyle">×</button>
            </div>
          </li>
        </ul>
      </div>
      
      <div class="container" :style="mapWidgetStyle">
        <div class="flex between" :style="routeSummaryStyle">
          <div class="summary-stats">
            <div class="stat">
              <span class="">Distance:</span>
              <span class="value">{{ totalDistance }} km</span>
            </div>
            <div class="stat">
              <span class="">Duration:</span>
              <span class="value">{{ totalDuration }} mins</span>
            </div>
            <div class="stat">
              <span class="">Stops:</span>
              <span class="value">{{ geocodedResults.length }}</span>
            </div>
          </div>
        </div>
        <div class="flex fill" :style="mapContainerStyle">
        </div>
      </div>
    </div>
  </template>
<script>
import gmaps from '../../utils/gmaps.js'
export default {
  name: 'maps',
  data() {
    return {
      // Store positions and results for each geocoded location
      positions: [],
      geocodedResults: [],
      map: null,
      google: null,
      routeInfo: null,
      editMode: false,
      editableStops: [],
      componentKey: 0,
      travelMode: 'DRIVING',
      // Performance optimization flags
      geocodingInProgress: false,
      routeCalculated: false
    }
  },
  props: {
    // Single address fallback (first element if locations not provided)
    address: { type: String },
    // Accept either an array of address strings or a comma‑separated string
    locations: { type: [Array, String], default: () => [] },
    description: { type: String },
    title: { type: [String, Number] },
    // Item type and id for updating
    type: { type: String },
    id: { type: String }
  },
  async mounted() {
    if (this.geocodingInProgress) return;
    
    this.geocodingInProgress = true;
    this.routeCalculated = false;
    
    try {
      this.google = await gmaps();
      const geocoder = new this.google.maps.Geocoder();
      this.map = new this.google.maps.Map(this.$el.querySelector('.flex.fill'), {
        mapTypeControl: false,
        streetViewControl: false,
        mapId: 'default', // mapId retained, styles removed to avoid warning
        // styles: [ ...original styles... ] // removed
      });

      // Create a simple marker with fallback to default icon
      const createMarker = (location, title) => {
        const contentDiv = document.createElement('div');
        const svg = `<svg viewBox="0 0 24 24" width="24" height="24" fill="#4285F4"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/></svg>`;
        contentDiv.innerHTML = svg;
        const marker = new this.google.maps.marker.AdvancedMarkerElement({
          position: location,
          map: this.map,
          content: contentDiv
        });
        marker.title = title;
        return marker;
      };

      // Use only the address prop from single.map.address
      let addressList = [];
      if (typeof this.address === 'string' && this.address.trim()) {
        addressList = this.address.split(/[\r\n,]+/).map(s => s.trim()).filter(s => s);
      } else {
        addressList = ['NYC'];
      }
      console.log('Address list for geocoding:', addressList);
      const geocodePromises = addressList.map(addr => new Promise((resolve, reject) => {
        geocoder.geocode({ address: addr }, (results, status) => {
          if (status !== 'OK' || !results[0]) {
            reject(status);
          } else {
            resolve({ result: results[0], address: addr });
          }
        });
      }));

      // Geocode all addresses with error handling
      const geocodeResults = await Promise.all(
        geocodePromises.map(p =>
          p.catch(err => {
            console.error('Geocoding failed:', err);
            return null; // Skip failed geocodes
          })
        )
      ).then(results => results.filter(r => r !== null)); // Remove failed entries

      console.log('Geocoded results:', geocodeResults);
      this.geocodedResults = geocodeResults;
      this.positions = geocodeResults.map(({ result }) => result.geometry.location);

      // Place markers for each location
      this.positions.forEach((pos, i) => {
        const marker = createMarker(pos, this.geocodedResults[i].address);
        // Add click listener to show address info
        marker.addListener('click', () => {
          const infoWindow = new this.google.maps.InfoWindow({
            content: `<div class="sub-container p4"><b>${this.geocodedResults[i].address}</b><br/>${this.geocodedResults[i].result.formatted_address}</div>`
          });
          infoWindow.open(this.map, marker);
        });
      });

      // Helper method to compute route – can be called after reorder
      this.computeRoute = () => {
        if (this.routeCalculated) return; // Prevent multiple route calculations
        
        console.log('Computing route for positions:', this.positions);
        this.routeCalculated = true;
        const directionsService = new this.google.maps.DirectionsService();
        const directionsRenderer = new this.google.maps.DirectionsRenderer({
          map: this.map,
          suppressMarkers: true, // Use our custom markers
          polylineOptions: {
            strokeColor: '#FF0000',
            strokeWeight: 4
          }
        });

        const waypoints = this.positions.slice(1, -1).map(pos => ({ location: pos, stopover: true }));
        const request = {
          origin: this.positions[0],
          destination: this.positions[this.positions.length - 1],
          waypoints,
          travelMode: this.google.maps.TravelMode[this.travelMode] || this.google.maps.TravelMode.DRIVING,
          optimizeWaypoints: true // Reorder for shortest route
        };

        directionsService.route(request, (result, status) => {
          if (status === this.google.maps.DirectionsStatus.OK) {
            directionsRenderer.setDirections(result);
            const route = result.routes[0];
            const summary = route.legs.map((leg, i) => ({
              start: this.geocodedResults[i]?.address || 'Start',
              end: this.geocodedResults[i + 1]?.address || 'End',
              distance: leg.distance.text,
              duration: leg.duration.text
            }));
            this.$emit('route-calculated', { summary, route });
            console.log('Route calculated:', summary);
            this.routeInfo = summary;
          } else {
            console.error('Directions request failed due to ' + status);
            this.$emit('route-error', status);
            // Still show markers even if directions fail
            const bounds = new this.google.maps.LatLngBounds();
            this.positions.forEach(pos => bounds.extend(pos));
            this.map.fitBounds(bounds, { top: 20, right: 20, bottom: 20, left: 20 });
          }
        });
      };

      // If we have at least two points, compute and display route (initial load)
      if (this.positions.length >= 2) {
        this.computeRoute();
      }

      // Adjust map view to show all markers with tight bounds
      if (this.positions.length > 0) {
        const bounds = new this.google.maps.LatLngBounds();
        this.positions.forEach(pos => bounds.extend(pos));
        this.map.fitBounds(bounds, { top: 20, right: 20, bottom: 20, left: 20 });

        // Add a listener to ensure bounds are applied after the map is fully loaded
        this.google.maps.event.addListenerOnce(this.map, 'idle', () => {
          this.map.fitBounds(bounds, { top: 20, right: 20, bottom: 20, left: 20 });
        });
      }
    } catch (error) {
      console.error(error);
    } finally {
      this.geocodingInProgress = false;
    }
  },
  computed: {
    mapWidgetStyle() {
      return {
        width: '100%',
        height: '300px',
        position: 'relative',
        marginBottom: '15px',
        borderRadius: '8px',
        overflow: 'hidden',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',

        display: 'flex',
        flexDirection: 'column'
      };
    },
    mapContainerStyle() {
      return {
        width: '100%',
        height: '250px',
        borderRadius: '0 0 8px 8px',
        flex: '1',
        marginTop: '50px'
      };
    },
    directionsButtonStyle() {
      const styles = this.$store.state.app.style;
      return {
        zIndex: '1000',
        background: styles.button || styles.success || '#4285F4',
        color: styles.color || 'white',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer',
      };
    },
    locationListStyle() {
      const styles = this.$store.state.app.style;
      return {
        listStyle: 'none',
        padding: '0',
        marginTop: '0',
        overflowY: 'auto',
        background: styles.container || 'transparent',
        color: styles.color || '#000',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        flex: '0 0 auto'
      };
    },
    locationItemStyle() {
      const styles = this.$store.state.app.style;
      return {
        padding: '10px 12px',
        background: styles['sub-container'] || '#f8f9fa',
        marginBottom: '2px',
        cursor: 'grab',
        color: styles.color || '#000',
        border: 'none',
        borderRadius: '4px',
        transition: 'all 0.3s ease',
        display: 'flex',
        alignItems: 'center',
        fontSize: '12px',
        fontWeight: '500',
        userSelect: 'none'
      };
    },
    routeTableData() {
      if (!this.routeInfo) return null;
      return this.routeInfo.map((leg) => ({
        'From': leg.start,
        'To': leg.end,
        'Distance': leg.distance,
        'Duration': leg.duration
      }));
    },
    controlsStyle() {
      return {
        display: 'flex',
        gap: '10px',
        marginBottom: '15px',
        alignItems: 'center'
      };
    },
    adjustButtonStyle() {
      const styles = this.$store.state.app.style;
      return {
        background: styles.button || styles.success || '#4285F4',
        color: 'white',
        border: 'none',
        padding: '8px 16px',
        borderRadius: '4px',
        cursor: 'pointer',
        fontSize: '14px',
        fontWeight: '500'
      };
    },
    addButtonStyle() {
      const styles = this.$store.state.app.style;
      return {
        background: styles.success || '#28a745',
        color: 'white',
        border: 'none',
        padding: '4px 8px',
        borderRadius: '4px',
        cursor: 'pointer',
        fontSize: '10px',
        marginLeft: '10px'
      };
    },
    stopInputStyle() {
      return {
        flex: '1',
        padding: '4px 6px',
        border: '1px solid #ccc',
        borderRadius: '3px',
        fontSize: '12px',
        margin: '0 6px'
      };
    },
    actionButtonStyle() {
      return {
        background: '#6c757d',
        color: 'white',
        border: 'none',
        padding: '2px 6px',
        borderRadius: '3px',
        cursor: 'pointer',
        fontSize: '10px',
        minWidth: '20px',
        margin: '0 1px'
      };
    },
    removeButtonStyle() {
      return {
        background: '#dc3545',
        color: 'white',
        border: 'none',
        padding: '2px 6px',
        borderRadius: '3px',
        cursor: 'pointer',
        fontSize: '10px',
        minWidth: '20px',
        margin: '0 1px'
      };
    },
    totalDistance() {
      if (!this.routeInfo || !Array.isArray(this.routeInfo) || this.routeInfo.length === 0) return '0';
      let total = 0;
      this.routeInfo.forEach(leg => {
        if (leg && leg.distance) {
          // Handle both text and value properties
          if (typeof leg.distance.value === 'number') {
            total += leg.distance.value;
          } else if (typeof leg.distance === 'string') {
            // Parse text like "1.2 km" or "0.8 mi"
            const match = leg.distance.match(/(\d+\.?\d*)/);
            if (match) {
              const value = parseFloat(match[1]);
              // Convert to meters if needed
              if (leg.distance.includes('km')) {
                total += value * 1000;
              } else if (leg.distance.includes('mi')) {
                total += value * 1609.34;
              } else {
                total += value;
              }
            }
          }
        }
      });
      return total > 0 ? (total / 1000).toFixed(1) : '0';
    },
    totalDuration() {
      if (!this.routeInfo || !Array.isArray(this.routeInfo) || this.routeInfo.length === 0) return '0';
      let total = 0;
      this.routeInfo.forEach(leg => {
        if (leg && leg.duration) {
          // Handle both text and value properties
          if (typeof leg.duration.value === 'number') {
            total += leg.duration.value;
          } else if (typeof leg.duration === 'string') {
            // Parse text like "5 mins" or "1 hour 30 mins"
            const match = leg.duration.match(/(\d+)/);
            if (match) {
              const value = parseInt(match[1]);
              // Convert to seconds if needed
              if (leg.duration.includes('hour')) {
                total += value * 3600;
              } else if (leg.duration.includes('min')) {
                total += value * 60;
              } else {
                total += value;
              }
            }
          }
        }
      });
      return total > 0 ? Math.round(total / 60) : '0';
    },
    routeSummaryStyle() {
      const styles = this.$store.state.app.style;
      return {
        background: styles.container || '#f8f9fa',

        borderRadius: '8px 8px 0 0',
        padding: '10px 15px',
        marginBottom: '0',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        position: 'absolute',
        top: '0',
        left: '0',
        right: '0',
        zIndex: '10'
      };
    },
    routeTableStyle() {
      return {
        width: '100%',
        marginTop: '15px'
      };
    },
    travelModeButtonsStyle() {
      return {
        display: 'flex',
        gap: '5px',
        marginRight: '10px'
      };
    }
  },
  watch: {
    address: {
      handler() {
        // Only refresh if we're not in edit mode to avoid conflicts
        if (!this.editMode) {
          this.refreshMap();
        }
      }
    }
    // Removed the $store.state.app.style watcher that was causing infinite loops
  },
  methods: {
  async refreshMap() {
    if (!this.google || !this.map || this.geocodingInProgress) return;

    this.geocodingInProgress = true;
    this.routeCalculated = false;

    try {
      const geocoder = new this.google.maps.Geocoder();

      // Clear existing markers and route info
      this.positions = [];
      this.geocodedResults = [];
      this.routeInfo = null;

      // Use only the address prop from single.map.address
      let addressList = [];
      if (typeof this.address === 'string' && this.address.trim()) {
        addressList = this.address.split(/[\r\n,]+/).map(s => s.trim()).filter(s => s);
      } else {
        addressList = ['NYC'];
      }

      console.log('Address list for geocoding:', addressList);
      const geocodePromises = addressList.map(addr => new Promise((resolve, reject) => {
        geocoder.geocode({ address: addr }, (results, status) => {
          if (status !== 'OK' || !results[0]) {
            reject(status);
          } else {
            resolve({ result: results[0], address: addr });
          }
        });
      }));

      // Geocode all addresses with error handling
      const geocodeResults = await Promise.all(
        geocodePromises.map(p =>
          p.catch(err => {
            console.error('Geocoding failed:', err);
            return null; // Skip failed geocodes
          })
        )
      ).then(results => results.filter(r => r !== null)); // Remove failed entries

      console.log('Geocoded results:', geocodeResults);
      this.geocodedResults = geocodeResults;
      this.positions = geocodeResults.map(({ result }) => result.geometry.location);

      // Create a simple marker with fallback to default icon
      const createMarker = (location, title) => {
        const contentDiv = document.createElement('div');
        const svg = `<svg viewBox="0 0 24 24" width="24" height="24" fill="#4285F4"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/></svg>`;
        contentDiv.innerHTML = svg;
        const marker = new this.google.maps.marker.AdvancedMarkerElement({
          position: location,
          map: this.map,
          content: contentDiv
        });
        marker.title = title;
        return marker;
      };

      // Place markers for each location
      this.positions.forEach((pos, i) => {
        const marker = createMarker(pos, this.geocodedResults[i].address);
        // Add click listener to show address info
        marker.addListener('click', () => {
          const infoWindow = new this.google.maps.InfoWindow({
            content: `<div class="sub-container p4"><b>${this.geocodedResults[i].address}</b><br/>${this.geocodedResults[i].result.formatted_address}</div>`
          });
          infoWindow.open(this.map, marker);
        });
      });

      // If we have at least two points, compute and display route
      if (this.positions.length >= 2) {
        this.computeRoute();
      }

      // Adjust map view to show all markers with tight bounds
      if (this.positions.length > 0) {
        const bounds = new this.google.maps.LatLngBounds();
        this.positions.forEach(pos => bounds.extend(pos));
        this.map.fitBounds(bounds, { top: 20, right: 20, bottom: 20, left: 20 });
      }
    } catch (error) {
      console.error('Error refreshing map:', error);
    } finally {
      this.geocodingInProgress = false;
    }
  },
    openDirections() {
      if (this.positions.length < 2) return;

      // Create Google Maps directions URL with all locations
      const allAddresses = this.geocodedResults.map(loc => loc.address);
      const directionsUrl = `https://www.google.com/maps/dir/${allAddresses.map(addr => encodeURIComponent(addr)).join('/')}`;

      window.open(directionsUrl, '_blank');
    },
    onDrop(event, index) {
      const fromIndex = event.dataTransfer.getData('text/plain');
      const toIndex = index;
      console.log(`Drag drop from ${fromIndex} to ${toIndex}`);
      if (fromIndex !== toIndex) {
        const movedItem = this.geocodedResults[fromIndex];
        this.geocodedResults.splice(fromIndex, 1);
        this.geocodedResults.splice(toIndex, 0, movedItem);
        console.log('New order:', this.geocodedResults.map(loc => loc.address));
        this.positions = this.geocodedResults.map(({ result }) => result.geometry.location);
        // Re‑calculate route after reorder
        if (this.positions.length >= 2) {
          this.computeRoute();
        }

        // Re-center map after reorder
        if (this.positions.length > 0) {
          const bounds = new this.google.maps.LatLngBounds();
          this.positions.forEach(pos => bounds.extend(pos));
          this.map.fitBounds(bounds, { top: 20, right: 20, bottom: 20, left: 20 });
        }

        this.$emit('route-reorder', this.geocodedResults);
      }
    },
    onDragStart(event, index) {
      console.log('Drag start index:', index);
      event.dataTransfer.setData('text/plain', index);
    },
    toggleEditMode() {
      if (this.editMode) {
        // Save changes
        this.saveStops();
      } else {
        // Enter edit mode
        this.editMode = true;
        this.editableStops = this.geocodedResults.map(loc => ({ address: loc.address }));
      }
    },
    addStop() {
      this.editableStops.push({ address: '' });
    },
    removeStop(index) {
      if (this.editableStops.length > 1) {
        this.editableStops.splice(index, 1);
      }
    },
    moveStopUp(index) {
      if (index > 0) {
        const stops = [...this.editableStops];
        [stops[index], stops[index - 1]] = [stops[index - 1], stops[index]];
        this.editableStops = stops;
      }
    },
    moveStopDown(index) {
      if (index < this.editableStops.length - 1) {
        const stops = [...this.editableStops];
        [stops[index], stops[index + 1]] = [stops[index + 1], stops[index]];
        this.editableStops = stops;
      }
    },
    updateStop(index) {
      // Trigger geocoding for this stop
      this.geocodeStop(index);
    },
    async geocodeStop(index) {
      const stop = this.editableStops[index];
      if (!stop.address.trim()) return;
      
      try {
        const geocoder = new this.google.maps.Geocoder();
        const result = await new Promise((resolve, reject) => {
          geocoder.geocode({ address: stop.address }, (results, status) => {
            if (status === 'OK' && results[0]) {
              resolve(results[0]);
            } else {
              reject(status);
            }
          });
        });
        
        stop.geocoded = result;
        stop.position = result.geometry.location;
      } catch (error) {
        console.error('Geocoding failed for stop:', stop.address, error);
      }
    },
    async saveStops() {
      // Filter out empty stops and geocode all remaining stops
      const validStops = this.editableStops.filter(stop => stop.address.trim());
      
      // Geocode all stops
      for (let i = 0; i < validStops.length; i++) {
        await this.geocodeStop(i);
      }
      
      // Update the address prop with the new addresses
      const newAddresses = validStops.map(stop => stop.address);
      const newAddressString = newAddresses.join(', ');
      
      // Update internal state
      this.geocodedResults = validStops.map(stop => ({
        address: stop.address,
        result: stop.geocoded,
        position: stop.position
      }));
      
      // Update positions array for map
      this.positions = validStops.map(stop => stop.position).filter(pos => pos);
      
      // Update the parent component's data - emit the full object structure
      this.$emit('input', {
        address: newAddressString
      });
      
      // Also emit individual updates for backward compatibility
      this.$emit('update:address', newAddressString);
      
      // If we have type and id, update the item directly
      if (this.type && this.id) {
        await this.$store.dispatch('update', {
          type: this.type,
          id: this.id,
          data: {
            map: {
              address: newAddressString
            }
          }
        });
      }
      
      this.editMode = false;
      
      // Recalculate route with new positions without refreshing the entire map
      if (this.positions.length >= 2) {
        this.computeRoute();
      }
    },
    setTravelMode(mode) {
      this.travelMode = mode;
      if (this.positions.length >= 2) {
        this.computeRoute();
      }
    },
    getTravelModeButtonStyle(mode) {
      const styles = this.$store.state.app.style;
      const isActive = this.travelMode === mode;
      return {
        background: isActive ? (styles.button || styles.success || '#4285F4') : '#f0f0f0',
        color: isActive ? 'white' : '#333',



        cursor: 'pointer',


        transition: 'all 0.2s ease'
      };
    }
  }
}
</script>
<style>
.gm-style-iw,
.gm-style-iw-c {
  padding: 0 !important;
}

.stop-actions {
  display: flex;
  gap: 2px;
  margin-left: auto;
}

.stop-actions button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.summary-stats {
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
}

.stat {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.stat .label {
  font-size: 11px;
  color: #666;
  font-weight: 500;
}

.stat .value {
  font-size: 13px;
  font-weight: 600;
  color: #333;
}

.location-list li:active {
  cursor: grabbing;
  transform: scale(1.02);
  box-shadow: 0 4px 8px rgba(0,0,0,0.2);
}

.location-list li:hover {
  background: #e9ecef;
}
</style>